import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../lib/api';
import {
  Box,
  Button,
  Heading,
  Text,
  Flex,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Center,
  VStack,
} from '@chakra-ui/react';
import BreadcrumbsBar from '../components/Breadcrumbs';
import RecentActivityFeed from '../components/RecentActivityFeed';
import OnlineStatusWidget from '../components/OnlineStatusWidget';
import GlobalSearchBar from '../components/GlobalSearchBar';
import { getUserInfo } from '../auth/keycloak';

type Me = { id?: number; email?: string; displayName?: string; role?: string };

export default function Home(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiGet<Me>('/api/me'),
  });
  const userInfo = getUserInfo();
  const isAdmin = userInfo?.roles?.includes('ADMIN');

  // Activity feed is visible to all authenticated users
  // Students will see limited activities (their own submissions + assignment changes)

  return (
    <>
      <Box maxW={{ base: '100%', xl: '1400px' }} mx="auto" px={{ base: 2, md: 4 }}>
        <Heading size={{ base: 'md', md: 'lg' }} mb={2}>
          {t('home.title')}
        </Heading>
        <BreadcrumbsBar />

        {/* Global Search Bar - Center */}
        <Center my={6}>
          <GlobalSearchBar size="lg" maxWidth="700px" showShortcut={true} />
        </Center>

        {isLoading ? (
          <Card mt={6}>
            <CardBody>
              <Center py={8}>
                <VStack spacing={3}>
                  <Spinner size="lg" color="blue.500" />
                  <Text color="gray.600">{t('common.loading')}</Text>
                </VStack>
              </Center>
            </CardBody>
          </Card>
        ) : error ? (
          <Alert status="error" mt={6}>
            <AlertIcon />
            <Box>
              <AlertTitle>{t('common.error')}</AlertTitle>
              <AlertDescription>{t('home.loadError')}</AlertDescription>
            </Box>
          </Alert>
        ) : (
          data && (
            <VStack spacing={6} align="stretch" mt={6}>
              {/* Welcome Card */}
              <Card>
                <CardBody px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
                  <Text fontSize={{ base: 'sm', md: 'md' }}>
                    {t('home.greeting')} {data.displayName || data.email}
                  </Text>
                  <Flex direction={{ base: 'column', sm: 'row' }} gap={3} mt={4}>
                    <Button
                      as={Link}
                      to="/profile"
                      size={{ base: 'sm', md: 'md' }}
                      w={{ base: 'full', sm: 'auto' }}
                    >
                      {t('home.profile')}
                    </Button>
                    {data.id && (
                      <Button
                        as={Link}
                        to={`/users/${data.id}`}
                        variant="outline"
                        size={{ base: 'sm', md: 'md' }}
                        w={{ base: 'full', sm: 'auto' }}
                      >
                        {t('home.viewUserDetails')}
                      </Button>
                    )}
                  </Flex>
                </CardBody>
              </Card>

              {/* Recent Activity Feed - All users (filtered by role on backend) */}
              <RecentActivityFeed />
            </VStack>
          )
        )}
      </Box>

      {/* Online Status Widget (Admin only) */}
      {isAdmin && <OnlineStatusWidget />}
    </>
  );
}
